import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  dismissJobChange,
  draftJobChange,
  getJobChangeHistory,
  getJobChanges,
  type JobChangeHistoryItemDto,
  type JobChangeItemDto,
  sendCongratsDm,
  serverErrorMessage,
} from '../../lib/api.js';
import { formatRelative } from '../../lib/format.js';
import { LIVE_POLL_MS } from '../../lib/poll.js';
import { useWorkSummary } from '../../lib/use-work-summary.js';
import { AiEvidenceStrip } from '../ui/AiEvidenceStrip.js';
import { HistoryFacets } from '../ui/HistoryFacets.js';
import { JobChip } from '../ui/JobChip.js';
import { QueuePage, QueueScoreboard, type QueueTab } from './QueuePage.js';

/**
 * R17 — the FIRST tenant ported onto the shared QueuePage. Was `JobChangesModal`
 * (now deleted); same data + gated send path, re-homed as the routed
 * `?view=job-changes` queue: change headlines in the list, the AI congrats draft
 * + Send DM in the canvas, "Draft all" in the bulk menu, Done-today scoreboard.
 */

function changeHeadline(item: JobChangeItemDto): string {
  const name = item.contact_name ?? 'A connection';
  const role = item.new_role ?? 'a new role';
  const company = item.new_company ?? 'a new company';
  if (item.kind === 'promotion') {
    return `${name} — promoted to ${role}${item.new_company ? ` at ${item.new_company}` : ''}`;
  }
  return `${name} — new role: ${role} at ${company}`;
}

export function JobChangesView() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<QueueTab>('queue');

  const feed = useQuery({
    queryKey: ['job-changes'],
    queryFn: ({ signal }) => getJobChanges({ signal }),
    // R17 review — the AI draft is produced async on the worker and this feed has
    // NO SSE backing, so a freshly-enqueued draft only surfaces on a poll. Poll
    // fast (3s) while any change still needs a draft; relax to 30s once settled.
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((i) => i.status === 'detected') ? 3_000 : LIVE_POLL_MS,
  });
  // batch-297 — the History tab: the durable congrats-DM record (sent + failed, operator +
  // auto). Fetched only when the tab is open.
  const history = useQuery({
    queryKey: ['job-change-history'],
    queryFn: ({ signal }) => getJobChangeHistory({ signal }),
    enabled: tab === 'history',
  });
  const work = useWorkSummary();

  const items = feed.data?.items ?? [];
  const draftsArmed = Boolean(feed.data?.drafts_enabled);
  const sendArmed = Boolean(feed.data?.send_enabled);
  const needDraft = useMemo(() => items.filter((i) => i.status === 'detected'), [items]);

  // Keep a selection (auto-select the first; drop it if it leaves the list).
  useEffect(() => {
    if (items.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (selectedId === null || !items.some((i) => i.id === selectedId)) {
      setSelectedId(items[0]?.id ?? null);
    }
  }, [items, selectedId]);

  const draftAll = useMutation({
    mutationFn: async () => {
      for (const i of needDraft) await draftJobChange(i.id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job-changes'] }),
  });

  return (
    <QueuePage<JobChangeItemDto>
      testId="job-changes-view"
      title="Job changes"
      count={items.length}
      items={items}
      getId={(i) => i.id}
      selectedId={selectedId}
      onSelect={setSelectedId}
      rowEstimate={64}
      isLoading={feed.isLoading}
      isError={feed.isError}
      onRetry={() => void feed.refetch()}
      emptyTitle="No job changes"
      emptyHint="They appear after a profile refresh picks up a connection's new role."
      tab={tab}
      onTabChange={setTab}
      capabilityBadge={
        <span data-testid="jc-capability">
          {sendArmed ? 'review · approve · send' : 'review only'}
        </span>
      }
      bulkMenu={
        draftsArmed && needDraft.length > 0 ? (
          draftAll.isPending ? (
            <JobChip label="Drafting…" testId="jc-draft-all-chip" />
          ) : (
            <>
              <button
                type="button"
                className="unified-overflow-item"
                data-testid="jc-draft-all"
                onClick={() => draftAll.mutate()}
              >
                <span>✨ Draft all ({needDraft.length})</span>
                <span className="unified-overflow-hint">Queue an AI congrats draft for each</span>
              </button>
              {/* QA L11 — a failed bulk draft is no longer a silent no-op. */}
              {draftAll.isError ? (
                <div className="unified-overflow-hint" data-testid="jc-draft-all-error">
                  ⚠ Draft-all failed — try again.
                </div>
              ) : null}
            </>
          )
        ) : null
      }
      scoreboard={
        <QueueScoreboard
          openCount={items.length}
          doneToday={work.data?.done_today?.job_changes ?? 0}
        />
      }
      renderRow={(item) => <JobChangeRowSummary item={item} />}
      renderCanvas={(item) =>
        item ? (
          // key by id so switching items remounts (resets the local draft text).
          <JobChangeCanvas
            key={item.id}
            item={item}
            draftsArmed={draftsArmed}
            sendArmed={sendArmed}
          />
        ) : (
          <p className="queue-canvas-empty">Select a job change to review its draft.</p>
        )
      }
      renderContext={(item) => (item ? <JobChangeContext item={item} /> : null)}
      renderHistory={() => (
        <JobChangeHistory rows={history.data?.items ?? []} loading={history.isLoading} />
      )}
    />
  );
}

function historyHeadline(r: JobChangeHistoryItemDto): string {
  const role = r.new_role ?? 'a new role';
  const company = r.new_company ?? 'a new company';
  return r.kind === 'promotion'
    ? `promoted to ${role}${r.new_company ? ` at ${r.new_company}` : ''}`
    : `new role: ${role} at ${company}`;
}

function JobChangeHistory({
  rows,
  loading,
}: {
  rows: JobChangeHistoryItemDto[];
  loading: boolean;
}) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [trigger, setTrigger] = useState<string | null>(null);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!q || (r.contact_name ?? '').toLowerCase().includes(q)) &&
        (!status || r.status === status) &&
        (!trigger || r.trigger === trigger),
    );
  }, [rows, search, status, trigger]);

  if (loading) return <p className="jc-muted">Loading…</p>;
  if (rows.length === 0)
    return <p className="jc-muted">No congrats DMs sent yet — sent messages will appear here.</p>;
  // Stable, collision-proof keys WITHOUT the array index (the history is display-only and
  // carries no id): content-derived + a per-content occurrence counter (biome noArrayIndexKey).
  const seen = new Map<string, number>();
  const keyed = filtered.map((r) => {
    const base = `${r.at ?? ''}|${r.contact_name ?? ''}|${r.trigger}|${r.status}|${(r.text ?? '').slice(0, 24)}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return { r, key: n === 0 ? base : `${base}#${n}` };
  });
  return (
    <>
      <HistoryFacets
        search={search}
        onSearch={setSearch}
        shown={filtered.length}
        total={rows.length}
        groups={[
          {
            label: 'Status',
            value: status,
            onChange: setStatus,
            options: [
              { value: 'sent', label: 'Sent' },
              { value: 'failed', label: 'Failed' },
            ],
          },
          {
            label: 'Trigger',
            value: trigger,
            onChange: setTrigger,
            options: [
              { value: 'operator', label: 'You sent' },
              { value: 'auto', label: 'Auto-sent' },
            ],
          },
        ]}
      />
      {keyed.length === 0 ? (
        <p className="jc-muted">No matches.</p>
      ) : (
        <ul className="jc-history" data-testid="jc-history">
          {keyed.map(({ r, key }) => (
            <li key={key}>
              <span className="jc-history-line1">
                <span className={`jc-kind jc-trigger-${r.trigger}`}>
                  {r.trigger === 'auto' ? 'Auto-sent' : 'You sent'}
                </span>
                <span className={`jc-dot jc-dot-${r.status === 'sent' ? 'ok' : 'bad'}`}>
                  {r.status}
                </span>
                <strong>{r.contact_name ?? 'A connection'}</strong>
                <span className="jc-history-change">· {historyHeadline(r)}</span>
                {r.at ? <time>{formatRelative(r.at)}</time> : null}
              </span>
              {r.status === 'failed' && r.error ? (
                <span className="jc-history-error">⚠ {r.error}</span>
              ) : null}
              {r.text ? <span className="jc-history-text">{r.text}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function StatusDot({ item }: { item: JobChangeItemDto }) {
  const s = item.draft?.status;
  const label =
    item.status === 'sent'
      ? 'Sent'
      : s === 'ready'
        ? 'Draft ready'
        : s === 'suppressed'
          ? 'AI declined'
          : s === 'failed'
            ? 'Draft failed'
            : 'Needs draft';
  const cls =
    item.status === 'sent'
      ? 'ok'
      : s === 'ready'
        ? 'ready'
        : s === 'failed'
          ? 'bad'
          : s === 'suppressed'
            ? 'muted'
            : 'pending';
  return <span className={`jc-dot jc-dot-${cls}`}>{label}</span>;
}

function JobChangeRowSummary({ item }: { item: JobChangeItemDto }) {
  return (
    <span className="jc-row">
      <span className="jc-row-headline">{changeHeadline(item)}</span>
      <span className="jc-row-meta">
        <span className="jc-kind">{item.kind === 'promotion' ? 'Promotion' : 'New company'}</span>
        <StatusDot item={item} />
        {item.detected_at ? <time>{formatRelative(item.detected_at)}</time> : null}
      </span>
    </span>
  );
}

function JobChangeContext({ item }: { item: JobChangeItemDto }) {
  return (
    <div className="jc-context" data-testid="jc-context">
      <h3>{item.contact_name ?? 'A connection'}</h3>
      <dl>
        <dt>Change</dt>
        <dd>{item.kind === 'promotion' ? 'Promotion' : 'New company'}</dd>
        {item.new_company ? (
          <>
            <dt>New company</dt>
            <dd>{item.new_company}</dd>
          </>
        ) : null}
        {item.new_role ? (
          <>
            <dt>New role</dt>
            <dd>{item.new_role}</dd>
          </>
        ) : null}
        {item.old_company && item.kind === 'new_company' ? (
          <>
            <dt>Was at</dt>
            <dd>{item.old_company}</dd>
          </>
        ) : null}
        {item.detected_at ? (
          <>
            <dt>Detected</dt>
            <dd>{formatRelative(item.detected_at)}</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

function JobChangeCanvas({
  item,
  draftsArmed,
  sendArmed,
}: {
  item: JobChangeItemDto;
  draftsArmed: boolean;
  sendArmed: boolean;
}) {
  const qc = useQueryClient();
  const readyText = item.draft?.status === 'ready' ? (item.draft.text ?? '') : '';
  const [text, setText] = useState(readyText);
  const [dirty, setDirty] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(item.status === 'sent');
  const lastDraftAt = useRef<string | null>(item.draft?.created_at ?? null);

  // Re-seed when a newer READY draft arrives (unless the operator edited it). Also
  // re-seed when the SELECTED item changes (this component is keyed by item.id).
  useEffect(() => {
    const d = item.draft;
    if (!d?.created_at || d.created_at === lastDraftAt.current) return;
    lastDraftAt.current = d.created_at;
    setDrafting(false);
    if (d.status === 'ready' && d.text && !dirty) setText(d.text);
  }, [item.draft, dirty]);

  const draftMut = useMutation({
    mutationFn: () => draftJobChange(item.id),
    onMutate: () => {
      setDrafting(true);
      setErr(null);
      // R17 review — an explicit Redraft should re-seed the box with the new draft
      // (the dirty-guard would otherwise pin the operator's old edit forever).
      setDirty(false);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job-changes'] }),
    onError: (e) => {
      setDrafting(false);
      setErr(serverErrorMessage(e, 'draft failed'));
    },
  });
  const dismissMut = useMutation({
    mutationFn: () => dismissJobChange(item.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job-changes'] }),
    onError: (e) => setErr(serverErrorMessage(e, 'dismiss failed')),
  });
  const sendMut = useMutation({
    mutationFn: () => sendCongratsDm(item.id, text.trim() || undefined),
    onMutate: () => setErr(null),
    onSuccess: () => {
      setSent(true);
      void qc.invalidateQueries({ queryKey: ['job-changes'] });
    },
    onError: (e) => setErr(serverErrorMessage(e, 'send failed')),
  });

  const status = item.draft?.status;

  return (
    <div className="jc-canvas" data-testid="jc-canvas">
      <div className="jc-canvas-head">
        <h2>{changeHeadline(item)}</h2>
        <button
          type="button"
          className="jc-dismiss"
          onClick={() => dismissMut.mutate()}
          disabled={dismissMut.isPending}
        >
          Dismiss
        </button>
      </div>

      {status === 'ready' ? (
        <>
          <AiEvidenceStrip confidence={item.draft?.confidence} factsUsed={item.draft?.facts_used} />
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setDirty(true);
            }}
            rows={4}
            className="jc-draft"
            aria-label="Congratulations draft"
          />
          <p className="jc-hint">
            {sendArmed
              ? 'Edit if needed, then Send DM — sends a 1:1 LinkedIn message.'
              : 'Review only — sending is not enabled.'}
          </p>
        </>
      ) : status === 'suppressed' ? (
        <p className="jc-suppressed">
          AI declined: {item.draft?.reason ?? 'no appropriate message'}
        </p>
      ) : status === 'failed' ? (
        <p className="jc-failed">Draft failed: {item.draft?.reason ?? 'error'}</p>
      ) : drafting ? (
        <p className="jc-muted">Drafting…</p>
      ) : (
        <p className="jc-muted">No draft yet.</p>
      )}
      {err ? <p className="jc-failed">{err}</p> : null}

      {draftsArmed || sendArmed ? (
        <div className="jc-actions">
          {draftsArmed ? (
            <button
              type="button"
              className="jc-btn"
              onClick={() => draftMut.mutate()}
              disabled={draftMut.isPending || drafting}
            >
              {status === 'ready' || status === 'suppressed' || status === 'failed'
                ? '✨ Redraft'
                : '✨ AI draft'}
            </button>
          ) : null}
          {sent || item.status === 'sent' ? (
            <span className="jc-sent" data-testid="jc-sent">
              Sent ✓
            </span>
          ) : sendArmed && status === 'ready' ? (
            <button
              type="button"
              className="jc-btn jc-send"
              data-testid="jc-send"
              onClick={() => sendMut.mutate()}
              disabled={sendMut.isPending || !text.trim()}
            >
              {sendMut.isPending ? 'Sending…' : 'Send DM'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
